// Pacific-day windows as UTC instants (America/Los_Angeles, DST-aware) and Pacific clock strings.
const TZ = 'America/Los_Angeles';
function pacificParts(d) {
    const f = new Intl.DateTimeFormat('en-US', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
    const p = Object.fromEntries(f.formatToParts(d).map((x) => [x.type, x.value]));
    return { y: Number(p.year), m: Number(p.month), day: Number(p.day) };
}
/** The UTC instant of 00:00 Pacific on the given Pacific calendar date. */
function pacificMidnight(y, m, day) {
    // Try both offsets (PDT -7, PST -8) and keep the one whose Pacific date and hour read back as y-m-day 00.
    for (const off of [7, 8]) {
        const t = new Date(Date.UTC(y, m - 1, day, off, 0, 0));
        const f = new Intl.DateTimeFormat('en-US', { timeZone: TZ, hour: '2-digit', hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit' });
        const p = Object.fromEntries(f.formatToParts(t).map((x) => [x.type, x.value]));
        if (Number(p.year) === y && Number(p.month) === m && Number(p.day) === day && Number(p.hour) === 0)
            return t;
    }
    return new Date(Date.UTC(y, m - 1, day, 8, 0, 0));
}
function addDays(y, m, day, n) {
    const t = new Date(Date.UTC(y, m - 1, day + n, 12));
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, day: t.getUTCDate() };
}
/** today; yesterday; week = the last 7 Pacific days including today; issues = the last 14 Pacific days including today. */
export function pacificWindow(name, now) {
    const t = pacificParts(now);
    const tomorrow = addDays(t.y, t.m, t.day, 1);
    const endToday = pacificMidnight(tomorrow.y, tomorrow.m, tomorrow.day).toISOString();
    const startOf = (n) => { const d = addDays(t.y, t.m, t.day, n); return pacificMidnight(d.y, d.m, d.day).toISOString(); };
    if (name === 'today')
        return { start: startOf(0), end: endToday };
    if (name === 'yesterday')
        return { start: startOf(-1), end: startOf(0) };
    if (name === 'week')
        return { start: startOf(-6), end: endToday };
    return { start: startOf(-13), end: endToday };
}
/** The last `n` Pacific calendar days ending today, oldest first, each as UTC instants (DST-aware): the Trends page's days. */
export function pacificDays(now, n) {
    const t = pacificParts(now);
    const pad = (x) => String(x).padStart(2, '0');
    const out = [];
    for (let i = n - 1; i >= 0; i--) {
        const d = addDays(t.y, t.m, t.day, -i);
        const e = addDays(t.y, t.m, t.day, -i + 1);
        out.push({ day: `${d.y}-${pad(d.m)}-${pad(d.day)}`, start: pacificMidnight(d.y, d.m, d.day).toISOString(), end: pacificMidnight(e.y, e.m, e.day).toISOString() });
    }
    return out;
}
/** The instant one working day after `t` (epoch ms): 24 hours of Monday to Saturday, Pacific (a Sunday does not count; DST-aware), so a time on
 *  Saturday 10 am ends Monday 10 am. NaN for NaN. */
export function oneWorkingDayAfter(t) {
    if (!Number.isFinite(t))
        return NaN;
    let cur = t, left = 24 * 3_600_000;
    while (left > 0) {
        const p = pacificParts(new Date(cur));
        const n = addDays(p.y, p.m, p.day, 1);
        const end = pacificMidnight(n.y, n.m, n.day).getTime();
        if (new Date(Date.UTC(p.y, p.m - 1, p.day)).getUTCDay() === 0) {
            cur = end;
            continue;
        }
        const take = Math.min(left, end - cur);
        cur += take;
        left -= take;
    }
    return cur;
}
/** "2:41 pm" in Pacific time. */
export function pacificClock(iso) {
    const d = new Date(iso.replace(/\+0000$/, 'Z'));
    if (Number.isNaN(d.getTime()))
        return '';
    return new Intl.DateTimeFormat('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit', hour12: true }).format(d)
        .replace(/\s?([AP])M$/i, (_m, a) => ` ${a.toLowerCase()}m`);
}
/** "Tue 10/6 2:41 pm" in Pacific time, for the timeline and older rows. */
export function pacificStamp(iso) {
    const d = new Date(iso.replace(/\+0000$/, 'Z'));
    if (Number.isNaN(d.getTime()))
        return '';
    const day = new Intl.DateTimeFormat('en-US', { timeZone: TZ, weekday: 'short', month: 'numeric', day: 'numeric' }).format(d).replace(',', '');
    return `${day} ${pacificClock(iso)}`;
}
/** "Fri 10/2" in Pacific time. */
export function pacificDay(iso) {
    const d = new Date(iso.replace(/\+0000$/, 'Z'));
    if (Number.isNaN(d.getTime()))
        return '';
    return new Intl.DateTimeFormat('en-US', { timeZone: TZ, weekday: 'short', month: 'numeric', day: 'numeric' }).format(d).replace(',', '');
}
/** When the case was opened (converted), Pacific: "Opened today 9:14 am", "Opened yesterday", "Opened 10/3", "Opened 12/31/25" (another
 * year). Empty for a missing or unreadable time. */
export function openedLabel(iso, now) {
    const d = new Date(String(iso ?? '').replace(/\+0000$/, 'Z'));
    if (!iso || Number.isNaN(d.getTime()))
        return '';
    const p = pacificParts(d), t = pacificParts(now), y = addDays(t.y, t.m, t.day, -1);
    if (p.y === t.y && p.m === t.m && p.day === t.day)
        return `Opened today ${pacificClock(d.toISOString())}`;
    if (p.y === y.y && p.m === y.m && p.day === y.day)
        return 'Opened yesterday';
    return `Opened ${p.m}/${p.day}${p.y === t.y ? '' : `/${String(p.y).slice(2)}`}`;
}
/** The case row's compact form, so DOL, case type and state stay visible: "Opened 9:14 am" today, else "Opened 10/7" ("/25" another year).
 * The long form (openedLabel) goes in the row's tooltip and the What happened header. */
export function openedShort(iso, now) {
    const d = new Date(String(iso ?? '').replace(/\+0000$/, 'Z'));
    if (!iso || Number.isNaN(d.getTime()))
        return '';
    const p = pacificParts(d), t = pacificParts(now);
    if (p.y === t.y && p.m === t.m && p.day === t.day)
        return `Opened ${pacificClock(d.toISOString())}`;
    return `Opened ${p.m}/${p.day}${p.y === t.y ? '' : `/${String(p.y).slice(2)}`}`;
}
/** "DOL 10/6/26" from a Salesforce date ('YYYY-MM-DD'). A date has no time zone, so it is read as text, never through Date (a Date
 * would shift it a day west of UTC). Anything that is not a real calendar date reads "no DOL". */
export function dolLabel(d) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(d ?? '').trim());
    if (!m)
        return 'no DOL';
    const [y, mo, da] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const real = new Date(Date.UTC(y, mo - 1, da));
    if (real.getUTCFullYear() !== y || real.getUTCMonth() !== mo - 1 || real.getUTCDate() !== da)
        return 'no DOL';
    return `DOL ${mo}/${da}/${String(y).slice(2)}`;
}
const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;
const pad2 = (x) => String(x).padStart(2, '0');
/** One Pacific calendar day (YYYY-MM-DD) as UTC instants, midnight to midnight, DST-aware; null for a malformed or impossible date. */
export function pacificDayWindow(day) {
    const m = YMD.exec(day);
    if (!m)
        return null;
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const check = new Date(Date.UTC(y, mo - 1, d));
    if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d)
        return null;
    const n = addDays(y, mo, d, 1);
    return { start: pacificMidnight(y, mo, d).toISOString(), end: pacificMidnight(n.y, n.m, n.day).toISOString() };
}
/** The Today page's ?day= (Cesar 2026-10-22): a Pacific date within the last `maxDays` (today included); otherwise today, with a note. */
export function todayDayOf(raw, now, maxDays = 30) {
    const t = pacificParts(now);
    const today = `${t.y}-${pad2(t.m)}-${pad2(t.day)}`;
    const oldest = (() => { const o = addDays(t.y, t.m, t.day, -(maxDays - 1)); return `${o.y}-${pad2(o.m)}-${pad2(o.day)}`; })();
    const back = (note) => ({ day: today, isToday: true, note });
    if (raw === null || raw === undefined || raw === '' || raw === today)
        return back(null);
    if (!pacificDayWindow(raw))
        return back('Showing today: that is not a date.');
    if (raw > today)
        return back('Showing today: that day has not happened yet.');
    if (raw < oldest)
        return back(`Showing today: the page goes back ${maxDays} days.`);
    return { day: raw, isToday: false, note: null };
}
//# sourceMappingURL=time.js.map